import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, Col, Input, Row } from "reactstrap";
import { getJobsheetTatLoading, getJobsheetTrend, getPaymentStatusLoading } from "src/store/actions";
import 'bootstrap/dist/css/bootstrap.min.css'
import { RootReducerState } from "src/store/reducers";
import DropdownInput from "src/components/UI/DropdownInput";
import { Form, Formik } from "formik";
import InputCom from "src/components/Common/Input";
import Button from "src/components/UI/Button";
import { LazyTableState } from "src/models/components/dataTableWrapperModel";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { CurrencyFormatter, formatDateMEL, formatDateTimeFormServerView, formatDateTOYYYYMMDD } from "src/helpers/common_helpers";
import { Column } from "primereact/column";
import UIButton from 'src/components/UI/Button'
import { Link } from "react-router-dom";
import { ROUTER } from "src/constants/routes";

interface TrendProps {
  date?: any
  realDate?: any
}

const convertTimesToSeconds = (seconds: any) => {
  if(seconds>0){
    return `${seconds} days`
  }else{
    return `Less than a day`
  }
};

const InvoiceTat = ({ date, realDate }: TrendProps) => {
  const dispatch = useDispatch();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(new Date().getDate() - 7);
  const filterDetails: DataTableFilterMeta = {
    InvoiceCreationStartDate: { value: formatDateTOYYYYMMDD(sevenDaysAgo), matchMode: "contains" },
    InvoiceCreationEndDate: { value: formatDateTOYYYYMMDD(new Date()), matchMode: "contains" },
    JobPrefix:{value:"all",matchMode:"contains"},
    type:{value:"all",matchMode:"contains"},
  };
  const [billStatus,SetBillStatus]=useState("all")
  
  const { paymentStatus,jobPrefixList } = useSelector((state: RootReducerState) => state.dashboard);
  
   const sortByInvoiceNumber = (a: any, b: any) => {
    return a.invoiceNumber?.localeCompare(b.invoiceNumber, undefined, { numeric: true, sensitivity: 'base' });
  };

  const paymentValue = useMemo(()=>{
    if(billStatus === "billed"){
      return {
        ...paymentStatus,
        items:paymentStatus?.items?.filter((data:any)=> data.isPaid != true).sort(sortByInvoiceNumber)
      }
    }else if(billStatus === "paid"){
      return {
        ...paymentStatus,
        items:paymentStatus?.items?.filter((data:any)=>data.isPaid == true).sort(sortByInvoiceNumber)
      }
    }else{
      return {
        ...paymentStatus,
      items:paymentStatus?.items?.sort(sortByInvoiceNumber)
      };
    }
  },[paymentStatus,billStatus])
  let initialLazyState: LazyTableState = {
    first: 0,
    rows: 1000,
    page: 1,
    sortField: "CreationTime",
    sortOrder: -1,
    filters: filterDetails,
  };

  const [lazyState, setLazyState] = useState<any>(initialLazyState);

  const options:any = {
    chart: {
      type: 'bar',
      stacked: true,
      stackType: 'normal',
      height: 350,
    },
    plotOptions: {
      bar: {
        horizontal: true,
      },
    },
    xaxis: {
      categories: [
        '60 Days',
        '45 Days',
        '30 Days',
        '15 Days',
        '7 Days',
        '24 Hours',
      ],
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val.toLocaleString('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
      },
    },
    colors: ['#f4a261', '#e76f51', '#6a040f'], // Example colors
    legend: {
      position: 'top',    
    },
  };
  const keyFunction = (billed:any)=>{
    if(billed){
      return "totalAmount"
    }else{
      return "paidAmount"
    }
  }
  const series =useMemo(()=>{
    if(paymentValue?.items?.length > 0){ 
    return[
    {
      name: '',
      data: [
        paymentValue?.items?.filter((data:any)=>data.paymentTat>=60)?.reduce((acc: any, curr: any) => acc + curr[keyFunction(billStatus)], 0), 
        paymentValue?.items?.filter((data:any)=>data.paymentTat<=45 && data.paymentTat>35)?.reduce((acc: any, curr: any) => acc + curr[keyFunction(billStatus)], 0), 
        paymentValue?.items?.filter((data:any)=>data.paymentTat<=35 && data.paymentTat>15)?.reduce((acc: any, curr: any) => acc + curr[keyFunction(billStatus)], 0), 
        paymentValue?.items?.filter((data:any)=>data.paymentTat<=15 && data.paymentTat>7)?.reduce((acc: any, curr: any) => acc + curr[keyFunction(billStatus)], 0), 
        paymentValue?.items?.filter((data:any)=>data.paymentTat<=7 && data.paymentTat>1)?.reduce((acc: any, curr: any) => acc + curr[keyFunction(billStatus)], 0), 
        paymentValue?.items?.filter((data:any)=>data.paymentTat<=1)?.reduce((acc: any, curr: any) => acc + curr[keyFunction(billStatus)], 0), 
        ],
    }
  ]}else{
    return[
      {
        name: '',
        data: [0,0,0,0,0,0],
      }
    ]
  }
},[paymentValue,billStatus])
  
  let dummyData =useMemo(()=>{
    let invoiceValue = paymentValue?.items?.reduce((acc: any, curr: any) => acc + curr.totalAmount, 0);
    let receivedValue = paymentValue?.items?.reduce((acc: any, curr: any) => acc + curr.paidAmount, 0);
    return [
      {
        name:"Invoice Qty",
        count:paymentValue?.items?.length,
        icon:"bx-pie-chart-alt-2"
    },
    {
        name:"Invoiced Value",
        count: CurrencyFormatter({value:invoiceValue}),
        icon:"bx-money"
    },
    {
        name:"Received Value",
        count:CurrencyFormatter({value:receivedValue}),
        icon:"bx-credit-card-alt"
    },
    {
        name:"Outstanding Value",
        count:CurrencyFormatter({value:invoiceValue - receivedValue}),
        icon:"bx-data"
    },
    {
        name:"Purchase Value",
        count:CurrencyFormatter({value:paymentValue?.purchaseValue?.purchaseValue}),
        icon:"bx-dollar"
    },
    {
        name:"Expense Value",
        count:CurrencyFormatter({value:paymentValue?.purchaseValue?.expenseValue}),
        icon:"bx-line-chart"
    },
    {
        name:"Need To Pay",
        count:CurrencyFormatter({value:invoiceValue - receivedValue}),
        icon:"bx-briefcase-alt-2"
    },
    ]
  },[paymentValue])

  const handleSubmit =(values:any)=>{
    debugger
    if(values.type=="billed"){
      SetBillStatus("billed")
    }else if(values.type=="paymentReceived"){
      SetBillStatus("paid")
    }else{
      SetBillStatus("all")
    }
    setLazyState((prev:any)=>{
      return{
        ...prev,
        filters:{
          ...prev.filters,
          InvoiceCreationStartDate:{value:values.InvoiceCreationStartDate},
          InvoiceCreationEndDate:{value:values.InvoiceCreationEndDate},
          JobPrefix:{value:values.JobPrefix},
          type:{value:values.type},
        }
      }
    })
  }

let colors= ["#0e86d4","#db1f48", "#76b947", "#bcece0", "#fad02c", "#01949a"]

  const initialvalue =useMemo(()=>{
    return{
      type:lazyState?.filters?.type?.value,
      JobPrefix:lazyState?.filters?.JobPrefix?.value,
      InvoiceCreationStartDate:lazyState?.filters?.InvoiceCreationStartDate?.value ? formatDateTOYYYYMMDD(lazyState?.filters?.InvoiceCreationStartDate?.value) : "",
      InvoiceCreationEndDate:lazyState?.filters?.InvoiceCreationEndDate?.value ? formatDateTOYYYYMMDD(lazyState?.filters?.InvoiceCreationEndDate?.value) : "",
    }
  },[lazyState]) 

  const callBackFunction = useCallback((lazyData:any)=>{
    debugger
    let modifiedLazyData = {
      ...lazyData,
      filters: {
        ...lazyData.filters,
        type:{value:""},
        JobPrefix:{value:lazyData.filters.JobPrefix?.value == "all" ? "" :lazyData.filters.JobPrefix?.value,matchMode:"contains"},
      },
    }
    dispatch(getPaymentStatusLoading(modifiedLazyData))
  },[])

  useEffect(()=>{
    callBackFunction(lazyState)
  },[lazyState])

    const ThreeDaysFilter = (days:any) =>{
      let today  = new Date()
      let dateBefore = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);
      setLazyState((state:any) => ({
        ...state,
        filters: {
          ...state.filters,
          InvoiceCreationStartDate: { value: formatDateTOYYYYMMDD(dateBefore), matchMode: "contains" },
          InvoiceCreationEndDate: { value: formatDateTOYYYYMMDD(today), matchMode: "contains" },
        },
      }))
    }
  const handleReset = (resetForm:any) =>{
    resetForm(initialvalue)
    SetBillStatus("all")
    let today  = new Date()
    let dateBefore = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    setLazyState((state:any) => ({
      ...state,
      filters: {
        ...state.filters,
        type:{ value: "all", matchMode: "contains" },
        JobPrefix:{ value: "all", matchMode: "contains" },
        InvoiceCreationStartDate: { value: formatDateTOYYYYMMDD(dateBefore), matchMode: "contains" },
        InvoiceCreationEndDate: { value: formatDateTOYYYYMMDD(today), matchMode: "contains" },
      },
    }))
  }

    const jobPrefixOptions = useMemo(()=>{
      let data = jobPrefixList?.map((item:any)=>({label:item.jobPrefix,value:item.jobPrefix}))
      return [
        {label:"All",value:"all"},
        ...data
      ]
    },[jobPrefixList])

  return (
    <React.Fragment>
      <Col xl={12}>
        <Card className="card-h-100">
          <CardBody>
            <div className="d-flex flex-wrap align-items-center mb-4">
              <h5 className="card-title me-2">Payment Status</h5>
              <div className="ms-auto">
                <div>
                  <b>{date}</b>
                </div>
              </div>
            </div>
            <Row className="align-items-center">
              <div
                className="d-flex w-100 flex-wrap"
                style={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  minWidth: "800px",
                }}
              >
                <div className="" style={{ width: "75%" }}>
                  <Formik
                    onSubmit={handleSubmit}
                    initialValues={initialvalue}
                    enableReinitialize
                  >
                    {({
                      values,
                      handleChange,
                      touched,
                      errors,
                      handleBlur,
                      resetForm,
                      reset,
                      isSubmitting,
                      ...rest
                    }: any) => {
                      return (
                        <Form onChange={handleChange}>
                          <Row>
                            <Col lg={2}>
                              <DropdownInput
                                options={[
                                  { label: "All", value: "all" },
                                  { label: "Billed", value: "billed" },
                                  { label: "Payment Received", value: "paymentReceived" },
                                ]}
                                name="type"
                                label="Type"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required={false}
                                value={values.type}
                                invalid={
                                  touched["type"] && errors["type"]
                                    ? true
                                    : false
                                }
                              />
                            </Col>
                            <Col lg={2} md={4} sm={12}>
                               <DropdownInput
                                options={jobPrefixOptions}
                                name="JobPrefix"
                                label="Job Prefix"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required={false}
                                value={values.JobPrefix}
                                invalid={
                                  touched["JobPrefix"] && errors["JobPrefix"] ? true : false
                                }
                            />
                            </Col>
                            <Col lg={2} md={4} sm={12}>
                              <label htmlFor="InvoiceCreationStartDate">
                                From
                              </label>
                              <Input
                                type="date"
                                required
                                name={"InvoiceCreationStartDate"}
                                value={values.InvoiceCreationStartDate}
                                onBlur={handleBlur}
                                max={
                                  values?.InvoiceCreationEndDate
                                    ? values?.InvoiceCreationEndDate
                                    : ""
                                }
                                onChange={handleChange}
                                invalid={false}
                              />
                            </Col>
                            <Col lg={2} md={4} sm={12}>
                              <label htmlFor="from">To</label>
                              <Input
                                type="date"
                                required
                                name={"InvoiceCreationEndDate"}
                                value={values.InvoiceCreationEndDate}
                                onBlur={handleBlur}
                                min={
                                  values?.InvoiceCreationStartDate
                                    ? values?.InvoiceCreationStartDate
                                    : ""
                                }
                                onChange={handleChange}
                                invalid={false}
                              />
                            </Col>
                            <Col
                              lg={2}
                              md={4}
                              sm={12}
                              className="align-self-end"
                            >
                              <div className="d-flex">
                                <Button
                                  className="secondary-btn me-2"
                                  label={"Reset"}
                                  type="button"
                                  onClick={() => handleReset(resetForm)}
                                />
                                <Button
                                  className="btn-primary btn-primary-shadow"
                                  label={"Search"}
                                  type="submit"
                                />
                              </div>
                            </Col>
                          </Row>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
                <div className="d-flex" style={{ width: "25%" }}>
                  <UIButton
                    label={"Last 7 Days"}
                    className={`btn-primary ms-2`}
                    type="button"
                    onClick={() => {
                      ThreeDaysFilter(7);
                    }}
                    disabled={false}
                  ></UIButton>
                  <UIButton
                    label={"Last 15 Days"}
                    className={`btn-primary ms-2`}
                    type="button"
                    onClick={() => {
                      ThreeDaysFilter(15);
                    }}
                    disabled={false}
                  ></UIButton>
                  <UIButton
                    label={"Last 30 Days"}
                    className={`btn-primary ms-2`}
                    type="button"
                    onClick={() => {
                      ThreeDaysFilter(30);
                    }}
                    disabled={false}
                  ></UIButton>
                </div>
              </div>
              <div className="chart-data">
                <Row>
                  <Col lg={12}>
                    <div className="d-flex flex-wrap mb-3 gap-2 mt-3">
                      {dummyData?.map((item: any, index: any) => (
                        <div className="data-card" key={index}>
                          <div className="d-flex flex-column justify-content-center align-items-start">
                            <div className="data-header">{item.name}</div>
                            <div className="data-body">{item.count}</div>
                          </div>
                          <div
                            className="icon"
                            style={{ backgroundColor: colors[index] }}
                          >
                            <i className={`bx ${item.icon}`}></i>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Col>
                  <Col lg={4}>
                    <ReactApexChart
                      options={options}
                      series={series}
                      type="bar"
                      height={400}
                    />
                  </Col>
                  <Col lg={8}>
                    <div
                      className="card ps-0 pe-0 dashboard-table"
                      id="table-border"
                    >
                      <DataTable
                        size="large"
                        value={paymentValue?.items}
                        showGridlines
                        lazy
                        scrollable
                        scrollHeight="400px"
                        tableStyle={{ minWidth: "50rem", maxHeight: "500px" }}
                      >
                        <Column
                          field="invoiceCreatedDate"
                          header="Invoice Date"
                          className=""
                          body={(rowData: any) =>
                            formatDateMEL(rowData?.invoiceCreatedDate)
                          }
                        ></Column>
                        <Column field="invoiceNumber" header="Invoice No" className=""
                          body={(rowData: any) => {
                            if (rowData.invoiceId) {
                              return (
                                <Link  to={`${ROUTER.INVOICE_VIEW}/${rowData.invoiceId}`} >{rowData.invoiceNumber ? rowData.invoiceNumber : "WIP"} </Link>
                              );
                            } else {
                              return rowData.invoiceNumber ? rowData.invoiceNumber : "WIP";
                            }
                          }}
                        ></Column>
                        <Column
                          field="billToName"
                          header="Invoiced To"
                          className=""
                        ></Column>
                        <Column
                          field="totalAmount"
                          header="Invoiced Value"
                          className=""
                        ></Column>
                        <Column
                          field="receivedOnDate"
                          header="Payment Recd On"
                          className=""
                          body={(rowData: any) =>
                            formatDateMEL(rowData?.receivedOnDate)
                          }
                        ></Column>
                        <Column
                          field="paymentTat"
                          header="Payment Tat"
                          className=""
                          body={(rowData: any) =>
                            convertTimesToSeconds(rowData?.paymentTat)
                          }
                        ></Column>
                      </DataTable>
                    </div>
                  </Col>
                </Row>
              </div>
            </Row>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default InvoiceTat;
